/**
 * timeUtils — ScheduleDraft UTC 时间拼装与表格展示格式化工具层。
 *
 * 本模块承接 Timetable 单元格编辑后的完整 datetime/HH:mm 合并、block time 展示和 UTC
 * 时间截取。它是 UI 无关的纯函数集合，供 row model、单元格编辑和后续编辑
 * 命令管道复用。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → components/editor/timetable/timetableRowModel.js (行模型), components/editor/timetable/useTimetableGridEditing.js (单元格提交), lib/flightRecalculationRules.js (时间联动)
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

function parseFullDateTimeValue(value) {
    if (!value) {
        return null;
    }

    if (typeof value === "string") {
        const dateTimeText = value.trim();
        if (!/\d{4}-\d{2}-\d{2}/.test(dateTimeText) || !/[T\s]\d{1,2}:\d{2}/.test(dateTimeText)) {
            return null;
        }

        const parsed = dayjs.utc(dateTimeText);
        return parsed.isValid() ? parsed.toISOString() : null;
    }

    if (typeof value === "object") {
        const parsed = dayjs.utc(value);
        return parsed.isValid() ? parsed.toISOString() : null;
    }

    return null;
}

/**
 * 构建 UTC datetime：完整日期时间值直接保留；HH:mm 文本则合并到已有 UTC 日期。
 * @param {string} baseValue - 原始 ISO 时间。
 * @param {string|Date|Object} timeValue - 完整 datetime 或 HH:mm 格式的时间文本。
 * @returns {string} 构建后的 ISO 时间；输入无效时返回原始值。
 */
export function buildDateTimeWithTime(baseValue, timeValue) {
    const fullDateTime = parseFullDateTimeValue(timeValue);
    if (fullDateTime) {
        return fullDateTime;
    }

    const timeText = String(timeValue || "").trim();
    if (!/^\d{2}:\d{2}$/.test(timeText)) {
        return baseValue;
    }

    const [hour, minute] = timeText.split(":").map(Number);
    if (hour > 23 || minute > 59) {
        return baseValue;
    }

    const baseTime = dayjs.utc(baseValue);
    if (!baseTime.isValid()) {
        return baseValue;
    }

    return baseTime.hour(hour).minute(minute).second(0).millisecond(0).toISOString();
}

/**
 * 格式化 block time 分钟数为紧凑展示文本。
 * @param {number|null|undefined} minutes - block time 分钟数。
 * @returns {string} 如 2h35 的展示文本，空值返回 --。
 */
export function formatBlockTime(minutes) {
    if (!minutes) {
        return "--";
    }

    return `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;
}

/**
 * 从 UTC 时间中提取 Timetable 单元格使用的 HH:mm 文本。
 * @param {string|null|undefined} value - ISO 时间。
 * @returns {string} HH:mm 文本，空值或无效值返回空字符串。
 */
export function formatTimeCell(value) {
    if (!value) {
        return "";
    }

    const parsed = dayjs.utc(value);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
}
